import { useEffect } from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";

import { Map as MaplibreMap } from "maplibre-gl";

import { SPATIAL_ANALYSIS_RESULT_DATASET_NAME } from "@/constants/dataset";
import {
  CATCHMENT_CENTROID_COLOR,
  CATCHMENT_CENTROID_COLUMN,
  CATCHMENT_RADIUS_COLOR,
  CATCHMENT_RADIUS_RESULT_SOURCE_ID
} from "@/constants/map";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import { RootState } from "@/store";
import { DatasetData } from "@/types/dataset";
import { SelectedFeature } from "@/types/map";
import { ECatchmentType } from "@/types/spatial";
import {
  ensureMapStyleReady,
  getRadiusPolygon,
  removeAllSourceLayerWithPrefix
} from "@/utils/map";
import { wktToGeoJSON } from "@/utils/wkt";

type UseCustomCatchmentRadiusProps = {
  map: MaplibreMap | null;
  isMapLoaded: boolean;
};

/** Radius in meters if the dataset uses a RADIUS custom catchment, else null. */
export const getCatchmentRadiusMeters = (
  rawDataset?: DatasetData
): number | null => {
  const customCatchment = rawDataset?.grid_config?.custom_catchment;
  if (
    customCatchment?.type !== ECatchmentType.BY_RADIUS.toUpperCase() ||
    !customCatchment.radius_m
  ) {
    return null;
  }
  return customCatchment.radius_m;
};

/** Centroid (WKT point) of the clicked cell as [lng, lat]. */
export const getCentroidCenter = (
  selectedFeature: SelectedFeature
): [number, number] | null => {
  const wkt = selectedFeature.properties?.[CATCHMENT_CENTROID_COLUMN];
  const geometry = typeof wkt === "string" ? wktToGeoJSON(wkt)?.geometry : null;
  if (!geometry || geometry.type !== "Point") return null;
  return geometry.coordinates as [number, number];
};

/** Removes the radius circle + centroid dot (shared source-id prefix). */
const clearCatchmentRadius = (map: MaplibreMap) => {
  removeAllSourceLayerWithPrefix(map, CATCHMENT_RADIUS_RESULT_SOURCE_ID, []);
};

/** Draws the radius circle (fill + outline) at the given center. */
const drawRadiusCircle = (
  map: MaplibreMap,
  center: [number, number],
  radiusMeters: number
) => {
  map.addSource(CATCHMENT_RADIUS_RESULT_SOURCE_ID, {
    type: "geojson",
    data: getRadiusPolygon(center, radiusMeters)
  });
  map.addLayer({
    id: `${CATCHMENT_RADIUS_RESULT_SOURCE_ID}-fill`,
    type: "fill",
    source: CATCHMENT_RADIUS_RESULT_SOURCE_ID,
    paint: {
      "fill-color": CATCHMENT_RADIUS_COLOR,
      "fill-opacity": 0.1
    }
  });
  map.addLayer({
    id: `${CATCHMENT_RADIUS_RESULT_SOURCE_ID}-outline`,
    type: "line",
    source: CATCHMENT_RADIUS_RESULT_SOURCE_ID,
    paint: {
      "line-color": CATCHMENT_RADIUS_COLOR,
      "line-width": 1
    }
  });
};

/** Draws the centroid dot at the center (call after the circle so it's on top). */
const drawCentroidCustomCatchment = (
  map: MaplibreMap,
  center: [number, number]
) => {
  const centroidSourceId = `${CATCHMENT_RADIUS_RESULT_SOURCE_ID}-centroid`;
  map.addSource(centroidSourceId, {
    type: "geojson",
    data: {
      type: "Feature",
      geometry: { type: "Point", coordinates: center },
      properties: {}
    }
  });
  map.addLayer({
    id: `${centroidSourceId}-point`,
    type: "circle",
    source: centroidSourceId,
    paint: {
      "circle-radius": 6,
      "circle-color": CATCHMENT_CENTROID_COLOR
    }
  });
};

/**
 * On clicking a "spatial analysis result" cell with RADIUS custom catchment,
 * draws a radius circle + centroid dot. Redraws on re-click, clears on
 * deselect / hidden layer / comparison / preview.
 */
export const useCustomCatchmentRadius = ({
  map,
  isMapLoaded
}: UseCustomCatchmentRadiusProps) => {
  const { isCoverageTypeEnabled } = useAppFeatures();
  const selectedFeature = useSelector(
    (state: RootState) => state.map.selectedFeature
  );
  const selectedDataset = useSelector((state: RootState) =>
    selectedFeature ? state.dataset.datasets[selectedFeature.source] : undefined
  );
  const selectedRawDataset = useSelector((state: RootState) =>
    selectedFeature
      ? state.dataset.rawDatasets[selectedFeature.source]
      : undefined
  );
  const isComparisonShown = useSelector(
    (state: RootState) => state.ui.showComparisonPopup.isShow
  );
  const [searchParams] = useSearchParams();
  const isPreviewMode = searchParams.get("preview") === "true";

  // Overlay shows in the result and summary views, not in comparison/preview.
  const isOverlaySuppressed = isComparisonShown || isPreviewMode;

  useEffect(() => {
    if (!map || !isMapLoaded) return;

    // Always start clean, then draw only when eligible.
    clearCatchmentRadius(map);

    if (!isCoverageTypeEnabled || isOverlaySuppressed || !selectedFeature) {
      return;
    }

    // Only the live "spatial analysis result" dataset.
    if (selectedDataset?.data?.name !== SPATIAL_ANALYSIS_RESULT_DATASET_NAME) {
      return;
    }

    // Hide with the dataset when its layer is toggled off.
    if (!(selectedDataset.properties?.isVisible ?? true)) return;

    const radiusMeters = getCatchmentRadiusMeters(selectedRawDataset);
    if (!radiusMeters) return;

    const center = getCentroidCenter(selectedFeature);
    if (!center) return;

    // Clear inside the callback: a replay must not double-add the source.
    const redrawCatchmentRadius = () => {
      clearCatchmentRadius(map);
      drawRadiusCircle(map, center, radiusMeters);
      drawCentroidCustomCatchment(map, center);
    };

    if (!ensureMapStyleReady(map, redrawCatchmentRadius)) return;

    redrawCatchmentRadius();

    // Clean up on unmount / before redraw.
    return () => clearCatchmentRadius(map);
  }, [
    map,
    isMapLoaded,
    isCoverageTypeEnabled,
    selectedFeature,
    selectedDataset,
    selectedRawDataset,
    isOverlaySuppressed
  ]);
};

export default useCustomCatchmentRadius;
